/**
 * Athena Workspace — Markdown Content Parser
 * Correção 3 Camada 3
 *
 * Parses LLM markdown responses into structured sections
 * for layout-aware rendering. No dependencies.
 */

// Split text into intro + named sections based on ## / ### / #### headers
export function parseMarkdownSections(text) {
  if (!text) return { intro: '', sections: [] };

  const lines = text.split('\n');
  const sections = [];
  let introLines = [];
  let current = null;
  let inIntro = true;

  for (const line of lines) {
    const h2 = line.match(/^##\s+(.+)/);
    const h3 = line.match(/^###\s+(.+)/);
    const h4 = line.match(/^####\s+(.+)/);
    // **Título** ou **Título:** sozinho na linha (modelo 8B usa isso como cabeçalho de seção)
    // [^*] permite dois-pontos dentro do título (**Modo de Preparo:**)
    // KV pairs como "**Key:** valor" NÃO batem pois têm texto depois do **
    const boldH = (!h2 && !h3 && !h4) ? line.match(/^\*\*([^*]{3,40})\*\*\s*$/) : null;
    const header = h2 || h3 || h4 || boldH;

    if (header) {
      inIntro = false;
      if (current) sections.push(current);
      current = {
        // strip trailing colon that 8B puts inside bold: **Title:** → "Title"
        title: cleanEmoji(header[1].trim()).replace(/:+$/, '').trim(),
        level: h2 ? 2 : 3,
        lines: [],
      };
    } else if (inIntro) {
      introLines.push(line);
    } else if (current) {
      current.lines.push(line);
    }
  }
  if (current) sections.push(current);

  // Trim blank lines from intro
  while (introLines.length && !introLines[0].trim()) introLines.shift();
  while (introLines.length && !introLines[introLines.length - 1].trim()) introLines.pop();

  return {
    intro: introLines.join('\n').trim(),
    sections: sections.map(s => ({ ...s, content: s.lines.join('\n').trim() })),
  };
}

// Extract numbered steps from section content
export function extractSteps(text) {
  if (!text) return null;
  const lines = text.split('\n').filter(l => l.trim());
  const steps = [];
  for (const line of lines) {
    const numbered = line.match(/^\d+[.)]\s+(.+)/);
    const bullet   = line.match(/^[-*•]\s+(.+)/);
    const match    = numbered || bullet;
    if (match) steps.push(stripBold(match[1].trim()));
  }
  return steps.length >= 2 ? steps : null;
}

// Extract bullet items (unordered list)
export function extractBullets(text) {
  if (!text) return null;
  const items = text.split('\n')
    .map(l => l.match(/^[-*•]\s+(.+)/))
    .filter(Boolean)
    .map(m => stripBold(m[1].trim()));
  return items.length >= 2 ? items : null;
}

// Extract key-value pairs: **Key**: Value  or  Key: Value
export function extractKeyValues(text) {
  if (!text) return null;
  const lines = text.split('\n').filter(l => l.trim());
  const pairs = [];
  for (const line of lines) {
    // Valor até 220 chars — cabe em um card de minmax(240px,1fr) sem cortar
    // a frase no meio da palavra (era 60, truncava descrições como "Dia N:").
    const bold = line.match(/\*\*([^*]{2,25})\*\*[:\s–-]+(.{1,220})/);
    const plain = !bold && line.match(/^([A-ZÀ-Ú][a-zà-ú][^:]{1,22}):\s+(.{1,220})/);
    const m = bold || plain;
    if (m) pairs.push({ key: m[1].trim(), value: stripBold(m[2].trim()) });
  }
  return pairs.length >= 2 ? pairs : null;
}

// Strip light markdown (bold/italic/inline-code only) from a string
export function stripBold(text) {
  return (text || '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1');
}

// Remove leading emojis from a section title
export function cleanEmoji(text) {
  if (!text) return text;
  // Remove trailing colons and trim whitespace - preserve emojis
  return text.replace(/:+$/, '').trim();
}

// Classify a section's content type for smart rendering
export function classifySectionContent(content) {
  if (!content) return 'empty';
  const lines = content.split('\n').filter(l => l.trim());
  const numbered = lines.filter(l => /^\d+[.)]\s/.test(l)).length;
  const bulleted  = lines.filter(l => /^[-*•]\s/.test(l)).length;
  const kv = lines.filter(l => /\*\*[^*]+\*\*[:\s]|^[A-ZÀ-Ú][a-zà-ú][^:]{1,22}:\s/.test(l)).length;

  if (numbered >= 2 && numbered >= bulleted) return 'steps';
  if (bulleted >= 2) return 'bullets';
  if (kv >= 2) return 'keyvalue';
  return 'text';
}

// Detect if section title matches a keyword group
export function sectionIs(title, ...keywords) {
  const t = (title || '').toLowerCase();
  return keywords.some(k => t.includes(k));
}

// ── Narrative structure parser (Camada 6.2) ──────────────────────────────────
// Returns { intro, sections, afterText, followUp }
// afterText: prose after last structured block in the last section
// followUp: final question paragraph if present

function _isStructuredBlock(block) {
  const t = block.trim();
  return (
    t.startsWith('>') ||
    /^[-*+•]\s/.test(t) ||
    /^\d+[.)]\s/.test(t) ||
    t.startsWith('```') ||
    t.startsWith('|') ||
    /\*\*[^*]{1,25}\*\*[:\s]/.test(t)
  );
}

function _extractTrailingProse(sectionContent) {
  const blocks = sectionContent.split(/\n\n+/);
  let lastStructIdx = -1;
  blocks.forEach((b, i) => { if (_isStructuredBlock(b)) lastStructIdx = i; });

  if (lastStructIdx < 0 || lastStructIdx >= blocks.length - 1) {
    // Prose-only section: if the last paragraph is a short question, extract it as followUp
    if (lastStructIdx < 0 && blocks.length >= 2) {
      const lastBlock = blocks[blocks.length - 1].trim();
      if (lastBlock.includes('?') && lastBlock.length < 220) {
        return {
          coreContent:   blocks.slice(0, -1).join('\n\n'),
          trailingProse: lastBlock,
        };
      }
    }
    return { coreContent: sectionContent, trailingProse: '' };
  }
  return {
    coreContent:   blocks.slice(0, lastStructIdx + 1).join('\n\n'),
    trailingProse: blocks.slice(lastStructIdx + 1).join('\n\n').trim(),
  };
}

export function parseNarrativeBlocks(content) {
  const parsed = parseMarkdownSections(content);
  if (parsed.sections.length === 0) {
    return { ...parsed, afterText: '', followUp: '' };
  }

  const last = parsed.sections[parsed.sections.length - 1];
  const { coreContent, trailingProse } = _extractTrailingProse(last.content);

  if (!trailingProse) {
    return { ...parsed, afterText: '', followUp: '' };
  }

  const paras = trailingProse.split(/\n\n+/).map(p => p.trim()).filter(Boolean);
  const lastPara = paras[paras.length - 1];
  let afterText = '';
  let followUp = '';

  if (lastPara && lastPara.includes('?')) {
    followUp  = stripBold(lastPara);
    afterText = paras.slice(0, -1).join('\n\n');
  } else {
    afterText = trailingProse;
  }

  const updatedSections = [
    ...parsed.sections.slice(0, -1),
    { ...last, content: coreContent, lines: coreContent.split('\n') },
  ];

  return {
    intro: parsed.intro,
    sections: updatedSections,
    afterText: afterText ? stripBold(afterText) : '',
    followUp,
  };
}
